import type { QueryClient } from "@tanstack/react-query";
import { invalidateMachines } from "@/hooks/use-machines";
import { api } from "@/lib/api";
import {
  type BatchReleaseResult,
  finishBatchRelease,
  finishReleaseError,
  finishReleaseSuccess,
  startReleaseToast,
} from "@/lib/release-toast";
import type { Machine } from "@/lib/types";

/** Fire a single-machine release + its toast. Non-blocking, no modal. */
export function runSingleRelease(machine: Machine, queryClient: QueryClient): void {
  const releaseToast = startReleaseToast(machine.name);
  api
    .releaseMachine(machine.id)
    .then(() => {
      releaseToast.stop();
      finishReleaseSuccess(releaseToast.id, machine.name);
    })
    .catch((error) => {
      releaseToast.stop();
      finishReleaseError(
        releaseToast.id,
        machine.name,
        error instanceof Error ? error.message : String(error),
      );
    })
    .finally(() => invalidateMachines(queryClient));
}

// Batch release + one consolidated toast. A batch of one delegates to the single path. Mirrors runBatchAcquire.
export function runBatchRelease(machines: Machine[], queryClient: QueryClient): void {
  const [firstMachine] = machines;
  if (!firstMachine) return;
  if (machines.length === 1) {
    runSingleRelease(firstMachine, queryClient);
    return;
  }

  const namesById = new Map(machines.map((machine) => [machine.id, machine.name]));
  const releaseToast = startReleaseToast(`${machines.length} machines`);
  api
    .batchAction(
      machines.map((machine) => machine.id),
      "release",
    )
    .then((data) => {
      releaseToast.stop();
      const results: BatchReleaseResult[] = data.results.map((result) => ({
        ok: result.ok,
        error: result.error,
        name: result.machine?.name ?? namesById.get(result.id) ?? `id=${result.id}`,
      }));
      finishBatchRelease(releaseToast.id, results);
    })
    .catch((error) => {
      releaseToast.stop();
      finishReleaseError(
        releaseToast.id,
        `${machines.length} machines`,
        error instanceof Error ? error.message : String(error),
      );
    })
    .finally(() => invalidateMachines(queryClient));
}
